import { vec3, mat4 } from 'gl-matrix';
import { Events as EVENTS, MetadataModules } from '../enums';
import type {
  WSIViewportProperties,
  Point3,
  Point2,
  ICamera,
  VOIRange,
  CPUIImageData,
  ViewportInput,
} from '../types';
import uuidv4 from '../utilities/uuidv4';
import * as metaData from '../metaData';
import { Transform } from './helpers/cpuFallback/rendering/transform';
import Viewport from './Viewport';
import { getOrCreateCanvas } from './helpers';
import { EPSILON } from '../constants';
import triggerEvent from '../utilities/triggerEvent';
import type { DataSetOptions } from '../types/IViewport';
import eventTarget from '../eventTarget';
import imageIdToURI from '../utilities/imageIdToURI';
import { getGenericViewportWSIDisplaySet } from './GenericViewport/genericViewportDisplaySetAccess';
import {
  addWSIMiniNavigationOverlayCss,
  type WSIClientLike,
  getDicomMicroscopyViewer,
  loadWSIData,
  type WSIImageDataMetadata,
  type WSIMapLike,
  type WSIMapViewLike,
  type WSITransformUtilitiesLike,
  type WSIViewerLike,
} from '../utilities/WSIUtilities';

let WSIUtilFunctions: WSITransformUtilitiesLike | null = null;
const EVENT_POSTRENDER = 'postrender';
const ANNOTATION_REMOVED = 'CORNERSTONE_TOOLS_ANNOTATION_REMOVED';
/**
 * A viewport which shows a microscopy view using the dicom-microscopy-viewer
 * library.  This viewport accepts standard CS3D annotations, and responds
 * similar to how the other types of viewports do for things like zoom/pan.
 *
 * This viewport required the `dicom-microscopy-viewer` import to be available
 * from the peerImport function in the CS3D init configuration.  See the
 * example `initDemo.js` for one possible implementation, but the actual
 * implementation of this will depend on your platform.
 */
class WSIViewport extends Viewport {
  public modality;
  // Viewport Data
  protected imageIds: string[];
  readonly uid;
  readonly renderingEngineId: string;

  private frameOfReferenceUID: string | null;

  // First is some specific metadata on the current image
  protected metadata: WSIImageDataMetadata;
  private microscopyElement: HTMLDivElement;

  protected map: WSIMapLike;
  private imageURISet: Set<string> = new Set();

  private internalCamera = {
    rotation: 0,
    centerIndex: [0, 0],
    extent: [0, -2, 1, -1],
    xSpacing: 1,
    ySpacing: 1,
    resolution: 1,
    zoom: 1,
  };

  private viewer: WSIViewerLike;
  private annotationRemovedListener: EventListener = (evt: Event) => {
    const { detail } = evt as CustomEvent<{
      annotation?: {
        metadata?: {
          FrameOfReferenceUID?: string;
          referencedImageId?: string;
          referencedImageURI?: string;
        };
      };
    }>;

    const metadata = detail?.annotation?.metadata;

    if (!metadata) {
      this.postrender();
      return;
    }

    const referencedImageURI =
      metadata.referencedImageURI ??
      (metadata.referencedImageId
        ? imageIdToURI(metadata.referencedImageId)
        : null);

    if (referencedImageURI && !this.hasImageURI(referencedImageURI)) {
      return;
    }

    const annotationFOR = metadata.FrameOfReferenceUID ?? null;

    if (annotationFOR && annotationFOR !== this.frameOfReferenceUID) {
      return;
    }

    this.postrender();
  };

  /**
   * The VOI Range is used to apply contrast/brightness adjustments to the image.
   */
  private voiRange: VOIRange = {
    lower: 0,
    upper: 255,
  };

  /**
   * feFilter is an inline string value for the CSS filter on the video
   * CSS filters can reference SVG filters, so for the typical use case here
   * the CSS filter is actually an link link to a SVG filter.
   */
  private feFilter: string;

  /**
   * An average white point value, used to color balance the image so that
   * the given white is mapped to [255,255,255] via multiplication per channel.
   */
  private averageWhite: [number, number, number];

  constructor(props: ViewportInput) {
    super({
      ...props,
      canvas: props.canvas || getOrCreateCanvas(props.element),
    });
    this.renderingEngineId = props.renderingEngineId;

    this.element.setAttribute('data-viewport-uid', this.id);
    this.element.setAttribute(
      'data-rendering-engine-uid',
      this.renderingEngineId
    );
    // Need to set the top level position as relative to make nested items
    // use absolute positioning internally.
    this.element.style.position = 'relative';
    this.microscopyElement = document.createElement('div');
    this.microscopyElement.setAttribute('class', 'DicomMicroscopyViewer');
    this.microscopyElement.id = uuidv4();
    this.microscopyElement.innerText = 'Initial';
    this.microscopyElement.style.background = 'grey';
    this.microscopyElement.style.width = '100%';
    this.microscopyElement.style.height = '100%';
    this.microscopyElement.style.position = 'absolute';
    this.microscopyElement.style.left = '0';
    this.microscopyElement.style.top = '0';
    const cs3dElement = this.element.firstElementChild;
    cs3dElement.insertBefore(this.microscopyElement, cs3dElement.childNodes[1]);

    this.addEventListeners();
    this.addWidget('DicomMicroscopyViewer', {
      getEnabled: () => !!this.viewer,
      setEnabled: () => {
        this.elementDisabledHandler();
      },
    });
    this.resize();
  }

  public static get useCustomRenderingPipeline() {
    return true;
  }

  private addEventListeners() {
    this.canvas.addEventListener(
      EVENTS.ELEMENT_DISABLED,
      this.elementDisabledHandler
    );
    eventTarget.addEventListener(
      ANNOTATION_REMOVED,
      this.annotationRemovedListener
    );
  }

  private removeEventListeners() {
    this.canvas.removeEventListener(
      EVENTS.ELEMENT_DISABLED,
      this.elementDisabledHandler
    );
    eventTarget.removeEventListener(
      ANNOTATION_REMOVED,
      this.annotationRemovedListener
    );
  }

  private elementDisabledHandler() {
    this.removeEventListeners();
    this.viewer?.cleanup();
    this.viewer = null;
    const cs3dElement = this.element.firstElementChild;
    cs3dElement.removeChild(this.microscopyElement);
    this.microscopyElement = null;
    this.imageURISet.clear();
  }

  // Sets the frame number - note according to DICOM, this is 1 based
  public async setFrameNumber(_frame: number) {
    // No-op right now, not sure what this will be
  }

  public setProperties(props: WSIViewportProperties) {
    if (props.voiRange) {
      this.setVOI(props.voiRange);
    }
  }

  public getProperties = (): WSIViewportProperties => {
    return {
      voiRange: { ...this.voiRange },
    };
  };

  /**
   * resetProperties resets the properties of the viewport to the default
   * values.  It is called by the resetViewer command in OHIF which is called when using
   * the reset toolbar button.
   */
  public resetProperties() {
    this.setProperties({
      voiRange: {
        lower: 0,
        upper: 255,
      },
    });
  }

  /**
   * setVOI sets the window level and window width for the image.  This is
   * used to set the contrast and brightness of the image.
   * feFilter is an inline string value for the CSS filter on the openLayers
   * CSS filters can reference SVG filters, so for the typical use case here
   * the CSS filter is actually an link link to a SVG filter.
   * the WSI viewport has two openlayers canvases; one for the main display and one for
   * the map overlay on the bottom left corner.
   */
  public setVOI(voiRange: VOIRange): void {
    this.voiRange = voiRange;
    const feFilter = this.setColorTransform(voiRange, this.averageWhite);
    const olCanvases = this.map
      .getViewport()
      .querySelectorAll('.ol-layers canvas');
    olCanvases.forEach((canvas) => {
      (canvas as HTMLCanvasElement).style.filter = feFilter;
    });
  }

  public setAverageWhite(averageWhite: [number, number, number]) {
    this.averageWhite = averageWhite;
    this.setColorTransform(this.voiRange, averageWhite);
  }

  protected getScalarData() {
    return null;
  }

  public computeTransforms() {
    const indexToWorld = mat4.create();
    const worldToIndex = mat4.create();

    mat4.fromTranslation(indexToWorld, this.metadata.origin);

    indexToWorld[0] = this.metadata.direction[0];
    indexToWorld[1] = this.metadata.direction[1];
    indexToWorld[2] = this.metadata.direction[2];

    indexToWorld[4] = this.metadata.direction[3];
    indexToWorld[5] = this.metadata.direction[4];
    indexToWorld[6] = this.metadata.direction[5];

    indexToWorld[8] = this.metadata.direction[6];
    indexToWorld[9] = this.metadata.direction[7];
    indexToWorld[10] = this.metadata.direction[8];

    mat4.scale(indexToWorld, indexToWorld, this.metadata.spacing);

    mat4.invert(worldToIndex, indexToWorld);
    return { indexToWorld, worldToIndex };
  }

  public getImageData(): CPUIImageData {
    const { metadata } = this;
    if (!metadata) {
      return null;
    }

    const { spacing } = metadata;

    const imageData = {
      getDirection: () => metadata.direction,
      getDimensions: () => metadata.dimensions,
      getRange: () => [0, 255],
      getScalarData: () => this.getScalarData(),
      getSpacing: () => metadata.spacing,
      worldToIndex: (point: Point3) => {
        return this.worldToIndex(point);
      },
      indexToWorld: (point: Point3) => {
        return this.indexToWorld(point);
      },
    };
    const imageDataReturn = {
      dimensions: metadata.dimensions,
      spacing,
      numberOfComponents: 3,
      origin: metadata.origin,
      direction: metadata.direction,
      metadata: {
        Modality: this.modality,
        FrameOfReferenceUID: this.frameOfReferenceUID,
      },

      hasPixelSpacing: this.hasPixelSpacing,
      calibration: this.calibration,
      preScale: {
        scaled: false,
      },
      scalarData: this.getScalarData(),
      imageData,
      // voxelManager is not in wsi.
    };

    // @ts-expect-error we need to fully migrate the voxelManager to the new system
    return imageDataReturn;
  }

  /**
   * Checks to see if the imageURI is currently being displayed.  The imageURI
   * may contain frame numbers according to the DICOM standard format, which
   * will be stripped to compare the base image URI, and then the values used
   * to check if that frame is currently being displayed.
   *
   * The DICOM standard allows for comma separated values as well, however,
   * this is not supported here, with only a single range or single value
   * being tested.
   *
   * For a single value, the time range +/- 5 frames is permitted to allow
   * the detection to actually succeed when nearby without requiring an exact
   * time frame to be matched.
   *
   * @param imageURI - containing frame number or range.
   * @returns
   */
  public hasImageURI(imageURI: string): boolean {
    if (!imageURI) {
      return false;
    }

    return this.imageURISet.has(imageURI);
  }

  public setCamera(camera: ICamera): void {
    const previousCamera = this.getCamera();
    const { parallelScale, focalPoint } = camera;
    const view = this.getView();
    const { xSpacing } = this.internalCamera;

    if (parallelScale) {
      const worldToCanvasRatio = this.element.clientHeight / parallelScale;
      const resolution = 1 / xSpacing / worldToCanvasRatio;

      view.setResolution?.(resolution);
    }

    if (focalPoint) {
      const newCanvas = this.worldToCanvas(focalPoint);
      const newIndex = this.canvasToIndex(newCanvas);
      view.setCenter([newIndex[0], newIndex[1]]);
    }
    const updatedCamera = this.getCamera();
    this.triggerCameraModifiedEventIfNecessary(previousCamera, updatedCamera);
  }

  /**
   * This function returns the imageID associated with either the current
   * frame being displayed, or the range of frames being played.  This may not
   * correspond to any particular imageId that has imageId metadata, as the
   * format is one of:
   * `<DICOMweb URI>/frames/<Start Frame>(-<End Frame>)?`
   * or
   * `<Other URI>[?&]frameNumber=<Start Frame>(-<EndFrame>)?`
   * for a URL parameter.
   *
   * @returns an imageID for video
   */
  public getCurrentImageId() {
    return this.imageIds[0];
  }

  public getFrameNumber() {
    return 1;
  }

  public getCamera(): ICamera {
    this.refreshRenderValues();
    const { resolution, xSpacing, centerIndex } = this.internalCamera;
    const canvasToWorldRatio = resolution * xSpacing;
    const canvasCenter = this.indexToCanvas([
      centerIndex[0],
      centerIndex[1],
      0,
    ]);
    const focalPoint = this.canvasToWorld(canvasCenter);

    return {
      parallelProjection: true,
      focalPoint,
      position: focalPoint,
      viewUp: [0, -1, 0],
      parallelScale: this.element.clientHeight * canvasToWorldRatio, // Reverse zoom direction back
      viewPlaneNormal: [0, 0, 1],
    };
  }

  public resetCamera = (): boolean => {
    return true;
  };

  /**
   * Gets the number of slices -  this will be the number of focal planes,
   * and not hte actual number of slices in the image sets.
   */
  public getNumberOfSlices = (): number => {
    return 1;
  };

  /**
   * The FOR for whole slide imaging is the frame of reference in the DICOM
   * metadata, and should be the same for all slices being viewed.
   */
  public getFrameOfReferenceUID = (): string => {
    return this.frameOfReferenceUID;
  };

  public resize = (): void => {
    const canvas = this.canvas;
    const { clientWidth, clientHeight } = canvas;

    // Set the canvas to be same resolution as the client.
    if (canvas.width !== clientWidth || canvas.height !== clientHeight) {
      canvas.width = clientWidth;
      canvas.height = clientHeight;
    }
    this.refreshRenderValues();
  };

  /**
   * Converts a slide coordinate to a image coordinate using WSI utils functions
   * @param point
   * @returns
   */
  public worldToIndexWSI(point: Point3): Point2 {
    if (!WSIUtilFunctions) {
      return;
    }
    const affine = this.viewer.getAffine();
    const pixelCoords = WSIUtilFunctions.applyInverseTransform({
      coordinate: [point[0], point[1]],
      affine,
    });
    return [pixelCoords[0], pixelCoords[1]] as Point2;
  }

  /**
   * Converts a image coordinate to a slide coordinate using WSI utils functions
   * @param point
   * @returns
   */
  public indexToWorldWSI(point: Point2): Point3 {
    if (!WSIUtilFunctions) {
      return;
    }
    const sliceCoords = WSIUtilFunctions.applyTransform({
      coordinate: [point[0], point[1]],
      affine: this.viewer.getAffine(),
    });
    return [sliceCoords[0], sliceCoords[1], 0] as Point3;
  }

  public worldToIndex(point: Point3): Point3 {
    const { worldToIndex: worldToIndexMatrix } = this.computeTransforms();
    const imageCoord = vec3.create();
    vec3.transformMat4(imageCoord, point, worldToIndexMatrix);
    return imageCoord as Point3;
  }

  public indexToWorld(point: Point3): Point3 {
    const { indexToWorld: indexToWorldMatrix } = this.computeTransforms();
    const worldPos = vec3.create();
    const point3D = vec3.fromValues(...point);
    vec3.transformMat4(worldPos, point3D, indexToWorldMatrix);
    return [worldPos[0], worldPos[1], worldPos[2]] as Point3;
  }

  /**
   * Converts a VideoViewport canvas coordinate to a video coordinate.
   *
   * @param canvasPosition - to convert to world
   * @returns World position
   */
  public canvasToWorld = (canvasPos: Point2): Point3 => {
    if (!this.metadata) {
      return;
    }
    // compute the pixel coordinate in the image
    const indexPoint = this.canvasToIndex(canvasPos);
    indexPoint[1] = -indexPoint[1]; // flip y axis to match canvas coordinates
    return this.indexToWorld(indexPoint);
  };

  /**
   * Converts and [x,y] video coordinate to a Cornerstone3D VideoViewport.
   *
   * @param  worldPos - world coord to convert to canvas
   * @returns Canvas position
   */
  public worldToCanvas = (worldPos: Point3): Point2 => {
    if (!this.metadata) {
      return;
    }
    const indexPoint = this.worldToIndex(worldPos);
    indexPoint[1] = -indexPoint[1]; // flip y axis to match canvas coordinates

    // pixel to canvas
    const canvasPoint = this.indexToCanvas([indexPoint[0], indexPoint[1], 0]);
    return canvasPoint;
  };

  /** GenericViewport compat shim – resolves dataId metadata and delegates to setDataIds. */
  public setDataList(
    entries: Array<{ dataId: string; options?: Record<string, unknown> }>
  ) {
    if (!entries.length) {
      return;
    }
    const { dataId } = entries[0];
    const dataSet = metaData.get('genericViewportDisplaySet', dataId) as
      | { imageIds?: string[]; options?: { webClient?: WSIClientLike } }
      | undefined;

    if (dataSet?.imageIds) {
      return this.setDataIds(dataSet.imageIds, {
        webClient: dataSet.options?.webClient,
      } as Parameters<typeof this.setDataIds>[1]);
    }

    return this.setDataIds([dataId]);
  }

  /**
   * This is a wrapper for setWSI to allow generic behaviour
   */
  public setDataIds(
    imageIds: string[],
    options?: DataSetOptions & {
      miniNavigationOverlay?: boolean;
      webClient: WSIClientLike;
    }
  ) {
    if (options?.miniNavigationOverlay !== false) {
      addWSIMiniNavigationOverlayCss();
    }
    const webClient =
      options?.webClient ||
      metaData.get(MetadataModules.WADO_WEB_CLIENT, imageIds[0]);
    if (!webClient) {
      throw new Error(
        `To use setDataIds on WSI data, you must provide metaData.webClient for ${imageIds[0]}.`
      );
    }

    // Returns the Promise from the child element.
    return this.setWSI(imageIds, webClient);
  }

  public async setWSI(imageIds: string[], client: WSIClientLike) {
    // Setting WSI data directly resets any display-set bookkeeping; the
    // setDisplaySets override re-records after calling this.
    this.clearDisplaySets();
    this.microscopyElement.style.background = 'black';
    this.microscopyElement.innerText = 'Loading';
    this.imageIds = imageIds;
    const DicomMicroscopyViewer = await getDicomMicroscopyViewer();
    WSIUtilFunctions ||= DicomMicroscopyViewer.utils;
    const loadedData = await loadWSIData({
      imageIds: this.imageIds,
      client,
      dicomMicroscopyViewer: DicomMicroscopyViewer,
    });

    this.imageURISet = loadedData.imageURISet;
    this.frameOfReferenceUID = loadedData.frameOfReferenceUID;
    this.metadata = loadedData.metadata;
    this.hasPixelSpacing = loadedData.metadata.hasPixelSpacing;

    // Construct viewer instance
    const viewer = new DicomMicroscopyViewer.viewer.VolumeImageViewer({
      client,
      metadata: loadedData.volumeImages,
      controls: ['overview', 'position'],
      retrieveRendered: false,
      bindings: {},
    });

    // Render viewer instance in the "viewport" HTML element
    viewer.render({ container: this.microscopyElement });

    viewer.deactivateDragPanInteraction();
    this.viewer = viewer;
    this.map = viewer.getMap();
    this.map.on(EVENT_POSTRENDER, this.postrender);
    this.resize();
    this.microscopyElement.innerText = '';
    Object.assign(this.microscopyElement.style, {
      '--ol-partial-background-color': 'rgba(127, 127, 127, 0.7)',
      '--ol-foreground-color': '#000000',
      '--ol-subtle-foreground-color': '#000',
      '--ol-subtle-background-color': 'rgba(78, 78, 78, 0.5)',
      background: 'none',
    });
  }

  /**
   * Mounts display sets on the viewport, mirroring the GenericViewport
   * `setDisplaySets` API. The `displaySetId` is resolved through the registered
   * generic-viewport dataset metadata (see `genericViewportDisplaySetMetadataProvider`)
   * to its WSI `imageIds` and `webClient`, which are loaded via `setWSI`.
   * Resolution and loading run inside {@link mountDisplaySets}, which records
   * the mounted entries after `setWSI` so {@link getDisplaySets} reports them.
   *
   * @param entries - display set entries to mount; the first is used as the WSI source.
   */
  public async setDisplaySets(
    ...entries: Array<{ displaySetId: string; options?: unknown }>
  ): Promise<void> {
    await this.mountDisplaySets(entries, async (entry) => {
      const dataSet = getGenericViewportWSIDisplaySet(entry.displaySetId);
      if (!dataSet?.imageIds?.length || !dataSet.options?.webClient) {
        throw new Error(
          `[WSIViewport] No registered WSI dataset (imageIds + webClient) for display set ${entry.displaySetId}`
        );
      }

      // Mirror setDataIds: the mini-navigation overlay CSS is injected here
      // (and only here on this path) so the overview/position controls are
      // styled. Honors the per-dataset opt-out; the helper is idempotent.
      if (dataSet.options.miniNavigationOverlay !== false) {
        addWSIMiniNavigationOverlayCss();
      }

      // Call the base loader non-virtually: WSIViewportLegacyAdapter overrides
      // setWSI to route through setDataIds -> setDisplaySets, which would recurse
      // back into here indefinitely. Bypassing the override loads the WSI directly.
      await WSIViewport.prototype.setWSI.call(
        this,
        dataSet.imageIds,
        dataSet.options.webClient
      );
    });
  }

  public postrender = () => {
    this.refreshRenderValues();
    triggerEvent(this.element, EVENTS.IMAGE_RENDERED, {
      element: this.element,
      viewportId: this.id,
      viewport: this,
      renderingEngineId: this.renderingEngineId,
    });
  };

  /**
   * Scrolls the image - for WSI, this changes the zoom ratio since different
   * images are used to represent different zoom levels, although this also
   * allows fractional zoom levels
   */
  public scroll(delta: number) {
    const camera = this.getCamera();
    this.setCamera({
      parallelScale: camera.parallelScale * (1 + 0.1 * delta),
    });
  }

  public getRotation = () => 0;

  protected canvasToIndex = (canvasPos: Point2): Point3 => {
    const transform = this.getTransform();
    transform.invert();
    const indexPoint = transform.transformPoint(canvasPos);
    return [indexPoint[0], indexPoint[1], 0] as Point3;
  };

  protected indexToCanvas = (indexPos: Point3): Point2 => {
    const transform = this.getTransform();
    return transform.transformPoint([indexPos[0], indexPos[1]]);
  };

  /** This can be implemented later when multi-slice WSI is supported */
  public getSliceIndex() {
    return 0;
  }

  /**
   * Gets the internal OpenLayers view object being rendered
   * Note this is not typeds right now, but might add typing later.
   */
  getView(): WSIMapViewLike | undefined {
    if (!this.viewer) {
      return;
    }
    // TODO - use a native method rather than accessing internals directly
    const map = this.viewer.getMap();
    // TODO - remove the globals setter
    const anyWindow = window as unknown as Record<string, unknown>;
    anyWindow.map = map;
    anyWindow.viewer = this.viewer;
    anyWindow.view = map?.getView();
    anyWindow.wsi = this;
    return map?.getView();
  }

  /**
   * Updates the internal camera rendering values from the underlying open layers
   * data.
   */
  private refreshRenderValues() {
    const view = this.getView();
    if (!view) {
      return;
    }
    const resolution = view.getResolution();
    if (!resolution || resolution < EPSILON) {
      return;
    }
    // The location of the center right now
    const centerIndex = view.getCenter();
    const extent = view.getProjection().getExtent();
    const rotation = view.getRotation();
    const zoom = view.getZoom();

    const {
      metadata: {
        spacing: [xSpacing, ySpacing],
      },
    } = this;

    // this means that each unit (pixel) in the world (video) would be
    // represented by n pixels in the canvas.
    const worldToCanvasRatio = 1 / resolution / xSpacing;

    Object.assign(this.internalCamera, {
      extent,
      centerIndex,
      worldToCanvasRatio,
      xSpacing,
      ySpacing,
      resolution,
      rotation,
      zoom,
    });
  }

  public customRenderViewportToCanvas = () => {};

  public getZoom() {
    return this.getView()?.getZoom();
  }

  public setZoom(zoom: number) {
    this.getView()?.setZoom(zoom);
  }

  /**
   * Returns the list of image Ids for the viewport.  Currently only
   * returns the first/primary image id.
   * @returns list of strings for image Ids
   */
  public getImageIds = (): Array<string> => {
    return [this.imageIds[0]];
  };

  /**
   * The transform here is from index to canvas points, so this takes
   * into account the scaling applied and the center location, but nothing to do
   * with world coordinate transforms.  Note that the 'index' values are often negative values with respect to the overall
   * image area, as that is what is used internally for the view.
   *
   * @returns A transform from index to canvas points
   */
  protected getTransform() {
    this.refreshRenderValues();
    const { centerIndex: center, resolution, rotation } = this.internalCamera;

    const halfCanvas = [this.canvas.width / 2, this.canvas.height / 2];
    const transform = new Transform();

    // Translate to the center of the canvas (move origin of the transform
    // to the center of the canvas)
    transform.translate(halfCanvas[0], halfCanvas[1]);
    // Difference in sign for x/y to account for screen coordinates
    transform.rotate(rotation);
    transform.scale(1 / resolution, -1 / resolution);
    transform.translate(-center[0], -center[1]);
    return transform;
  }

  public getViewReferenceId(): string {
    return `imageId:${this.getCurrentImageId()}`;
  }

  public getCurrentImageIdIndex() {
    return 0;
  }
}

export default WSIViewport;
